import { useCallback, useRef } from 'react';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Loads Razorpay's Checkout script once per document and resolves when the
 * `Razorpay` global exists.
 *
 * Loaded on demand rather than in index.html: the script is third-party, most
 * partners never open a top-up, and every operator screen would otherwise pay
 * for it on first paint.
 *
 * Concurrent callers share one <script>. The promise is cached on the module,
 * not in component state, so a modal that unmounts and reopens does not refetch
 * — and a failed load is NOT cached, so a partner on a flaky connection can
 * retry instead of being stuck with a rejected promise for the life of the tab.
 */
let loader = null;

export const loadRazorpayCheckout = () => {
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    const script = existing || document.createElement('script');

    script.onload = () => (window.Razorpay ? resolve(window.Razorpay) : reject(new Error('Checkout loaded without the Razorpay global')));
    script.onerror = () => reject(new Error('Could not reach Razorpay'));

    if (!existing) {
      script.src = CHECKOUT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  }).catch((error) => {
    // Let the next attempt try again rather than serving this rejection forever.
    loader = null;
    throw error;
  });

  return loader;
};

/**
 * Opens Razorpay Checkout for an order this app already created.
 *
 * ── ⭐ ONLY UPI AND CARDS ARE OFFERED ──────────────────────────────────────
 *
 * Not cosmetics. The booking engine checks the method Razorpay reports AFTER
 * capture against {upi, card} and refuses anything else — so a partner who pays
 * by netbanking, wallet, EMI or PayLater has their money taken and their wallet
 * NOT credited, and somebody has to refund them by hand. Showing a method the
 * server will refuse is the expensive failure here, not the safe one.
 *
 * ⚠️ This is the second line of defence, not the control. A Razorpay order is
 * method-agnostic, so a crafted client can ignore these blocks entirely; the
 * server-side check is what actually holds. Disabling the unwanted methods in
 * the Razorpay dashboard is the third, and the only one a partner cannot route
 * around by accident.
 *
 * ⚠️ AN INSTRUMENT NOT ENABLED ON THE ACCOUNT VANISHES SILENTLY. If a block
 * names a method the account does not have, Checkout drops the WHOLE block
 * rather than falling back — UPI disappeared from the sheet during testing with
 * nothing in any log to explain it. Both methods here are confirmed enabled
 * (GET /v1/preferences reports upi and card). Before adding a third, or before
 * the first live payment, check the LIVE account the same way: a partner who
 * loses UPI loses the method most of them will reach for.
 *
 * ⚠️ Do NOT pin `flows` on the UPI instrument. `flows: ['collect']` looks like
 * the way to get the "enter your UPI ID" field and instead empties the block on
 * desktop. Checkout picks the flow from the DEVICE — a QR on desktop, the VPA
 * field and app-intent links on a phone — which also means a desktop browser
 * will never show you what a partner on mobile sees. Test UPI on a real phone.
 */
export default function useRazorpayCheckout() {
  // Held so a caller can close the sheet itself — an unmounting modal must not
  // leave Checkout open over a screen that no longer knows about it.
  const instance = useRef(null);

  const open = useCallback(async ({ order, prefill, onHandoff, onDismiss, onFailed }) => {
    const Razorpay = await loadRazorpayCheckout();

    const rzp = new Razorpay({
      key: order.razorpayKeyId,
      order_id: order.razorpayOrderId,
      // Paise, straight from the engine. Never computed here.
      amount: order.razorpayAmount,
      currency: order.currencyCode,
      name: 'Zenvoya',
      description: 'Wallet top-up',
      config: {
        display: {
          blocks: {
            upi: { name: 'UPI', instruments: [{ method: 'upi' }] },
            card: { name: 'Cards', instruments: [{ method: 'card' }] },
          },
          sequence: ['block.upi', 'block.card'],
          // Without this the default blocks are appended BELOW ours, so
          // netbanking and the rest come back anyway.
          preferences: { show_default_blocks: false },
        },
      },
      // The portal holds the partner's profile; the booking engine deliberately
      // does not and returns no prefill of its own, so these never travel
      // through the money API.
      prefill,
      handler: (response) => onHandoff?.(response),
      modal: {
        // Closing the sheet is not a cancellation. A UPI payment approved on a
        // phone lands regardless, so the caller keeps polling either way.
        ondismiss: () => onDismiss?.(),
      },
    });

    rzp.on('payment.failed', (event) => onFailed?.(event?.error));
    instance.current = rzp;
    rzp.open();
    return rzp;
  }, []);

  const close = useCallback(() => {
    try {
      instance.current?.close();
    } catch {
      // Already closed, or Checkout tore itself down first. Nothing to recover.
    }
    instance.current = null;
  }, []);

  return { open, close };
}
